// placeholderTitleRename.js

/**
 * Platform / bookkeeping root fields — never surface as Activity updates.
 * Keep in sync with:
 * - functions/utils/nodes/activity.js `IGNORED_NODE_ROOT_KEYS`
 * - functions/utils/nodes/classifyNodeChange.js `META_ONLY_ROOT_KEYS`
 * - functions/utils/nodes/activityPlaceholderTitle.js
 */
export const IGNORED_ACTIVITY_FIELD_ROOTS = Object.freeze([
  '_automation',
  '_urlEnrichment',
  'accessScope',
  'ancestryPath',
  'autokey',
  'autonumber',
  'baseKey',
  'commentCount',
  'coverStoragePath',
  'dateCreated',
  'dateModified',
  'dateUpdated',
  'favoriteCount',
  'index',
  'likeCount',
  'missionRuns',
  'ownerUserKey',
  'parentTitle',
  'parentType',
  'peopleKeys',
  'publicPath',
  'referrerCount',
  'shareCount',
  'sourceInstanceId',
  'sourceInstanceIds',
  'updatedBy',
  'updatedByUser',
  'user',
  'viewCount',
]);

/**
 * Leaf keys that can appear under `data_*` (e.g. ClickUp fingerprint merge)
 * and should never surface as Activity updates.
 */
export const IGNORED_ACTIVITY_FIELD_LEAVES = Object.freeze([
  '_urlEnrichment',
  'accessScope',
  'dateCreated',
  'dateUpdated',
  'urlEnrichment',
  'user',
  'sourceInstanceId',
  'sourceInstanceIds',
  'originUpdatedAt',
  'customFields',
  'assigneeEmails',
  'assigneeSummaries',
  'taskId',
  'listId',
  'listName',
  'spaceId',
  'statusType',
  'closed',
  'customItemId',
  'dateClosed',
  'commentsHydrated',
  'attachmentsHydrated',
]);

const IGNORED_ACTIVITY_FIELD_ROOTS_LOWER = new Set(
  IGNORED_ACTIVITY_FIELD_ROOTS.map((key) => key.toLowerCase())
);

const IGNORED_ACTIVITY_FIELD_LEAVES_LOWER = new Set(
  IGNORED_ACTIVITY_FIELD_LEAVES.map((key) => key.toLowerCase())
);

const IGNORED_ACTIVITY_FIELD_TOKENS = new Set(
  [...IGNORED_ACTIVITY_FIELD_ROOTS, ...IGNORED_ACTIVITY_FIELD_LEAVES]
    .map((key) => String(key || '').replace(/[^a-zA-Z0-9]/g, '').toLowerCase())
    .filter(Boolean)
);

/**
 * @param {unknown} fieldPath
 * @returns {boolean}
 */
export function isIgnoredActivityFieldPath(fieldPath) {
  const safePath = String(fieldPath || '').trim();
  if (!safePath) return false;
  const segments = safePath.split('.');
  const rootKey = segments[0] || '';
  const leafKey = segments[segments.length - 1] || '';
  if (IGNORED_ACTIVITY_FIELD_ROOTS_LOWER.has(rootKey.toLowerCase())) return true;
  return IGNORED_ACTIVITY_FIELD_LEAVES_LOWER.has(leafKey.toLowerCase());
}

/**
 * True when title is the create placeholder "New {TypeLabel}".
 *
 * @param {unknown} title
 * @param {unknown} typeLabel
 * @returns {boolean}
 */
export function isDefaultNewNodeTitle(title, typeLabel) {
  const normalizedTitle = String(title || '').trim();
  const normalizedLabel = String(typeLabel || '').trim();
  if (!normalizedTitle || !normalizedLabel) return false;
  return normalizedTitle.toLowerCase() === `new ${normalizedLabel}`.toLowerCase();
}

/**
 * True when a field-change is the first rename away from "New {TypeLabel}".
 *
 * @param {object|null|undefined} change
 * @param {unknown} typeLabel
 * @returns {boolean}
 */
export function isPlaceholderTitleRename(change, typeLabel) {
  const fieldPath = String(change?.fieldPath || change?.field || '').trim();
  if (fieldPath !== 'title') return false;

  const previousValue = change?.previousValue;
  const newValue = change?.newValue;
  if (typeof previousValue !== 'string' || typeof newValue !== 'string') return false;
  if (!isDefaultNewNodeTitle(previousValue, typeLabel)) return false;
  return previousValue.trim() !== newValue.trim();
}

/**
 * True when a field-change is platform bookkeeping (view counts, paths, etc.).
 *
 * @param {object|null|undefined} change
 * @returns {boolean}
 */
export function isIgnoredSystemFieldChange(change) {
  return isIgnoredActivityFieldPath(change?.fieldPath || change?.field);
}

/**
 * True when a field-change should be omitted from Activity copy / feed rows.
 *
 * @param {object|null|undefined} change
 * @param {unknown} typeLabel
 * @returns {boolean}
 */
export function shouldOmitActivityFieldChange(change, typeLabel) {
  return isPlaceholderTitleRename(change, typeLabel) || isIgnoredSystemFieldChange(change);
}

/**
 * Heuristic for legacy events that only stored human copy like "Updated ViewCount."
 *
 * @param {unknown} text
 * @returns {boolean}
 */
export function isIgnoredActivitySummaryText(text) {
  const normalized = String(text || '').trim();
  if (!normalized) return false;

  // "Updated ViewCount." or "set the ancestrypath to …"
  const match = normalized.match(
    /^(?:updated|set the)\s+(.+?)(?:\s+to\b.*)?\.?$/i,
  );
  if (!match) return false;

  const token = String(match[1] || '')
    .replace(/[^a-zA-Z0-9]/g, '')
    .toLowerCase();
  if (!token) return false;

  return IGNORED_ACTIVITY_FIELD_TOKENS.has(token);
}

/**
 * Whether a stored activity event should appear in the feed.
 *
 * @param {object|null|undefined} event
 * @param {{ typeLabel?: string }} [options]
 * @returns {boolean}
 */
export function shouldIncludeActivityEvent(event, { typeLabel = '' } = {}) {
  if (!event) return false;

  const kind = String(event.kind || '').trim();
  const type = String(event.type || '').trim();
  if (kind === 'created' || type === 'node_created') return true;

  const isFieldChange = kind === 'field-changed' || type === 'node_field_changed';
  if (!isFieldChange) return true;

  const changes = Array.isArray(event.changes) ? event.changes : [];
  if (changes.length > 0) {
    return changes.some((change) => !shouldOmitActivityFieldChange(change, typeLabel));
  }

  const fieldPath = String(event.field || event.fieldPath || '').trim();
  if (fieldPath) {
    return !shouldOmitActivityFieldChange({
      fieldPath,
      previousValue: event.previousValue,
      newValue: event.newValue,
    }, typeLabel);
  }

  const summaries = [
    ...(Array.isArray(event.changeSummaries) ? event.changeSummaries : []),
    ...(Array.isArray(event.promptHints) ? event.promptHints : []),
  ]
    .map((entry) => String(entry || '').trim())
    .filter(Boolean);

  if (summaries.length > 0) {
    return summaries.some((summary) => !isIgnoredActivitySummaryText(summary));
  }

  return true;
}
